import React, { Component } from 'react';
import { toastr } from 'react-redux-toastr';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { isDirty } from 'redux-form';

import {
  validateForm,
  getFormById,
  getFormPdf,
  getPdfView,
  showAccountWithholdingModal,
  hideAccountWithholdingModal,
  showImyTreeWithholding,
  getStorageSetting
} from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import BlankPage from 'components/Blanks/BlankPage';
import RedirectButton from 'components/Common/Buttons/RedirectButton';
import NotFoundPage from 'components/Errors/NotFoundPage';
import ValidationResultsSection from 'components/ValidationResultsSection';
import ScrollPdfViewer from 'components/ScrollPdfViewer';
import SupportingDocs from 'components/SupportingDocs';
import FormReviewerComments from 'components/FormReviewerComments';
import IndividualValidationOverrides from 'components/IndividualValidationOverrides';
import WithholdingRatesModal from 'components/Modals/WithholdingRatesModal';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

import {
  AsyncBlankBene,
  AsyncBlankBene2017,
  AsyncBlankBene2021,
  AsyncBlankBen,
  AsyncBlankBen2014,
  AsyncBlankBen2021,
  AsyncBlankEci,
  AsyncBlankEci2017,
  AsyncBlankEci2021,
  AsyncBlankExp,
  AsyncBlankExp2017,
  AsyncBlankImy,
  AsyncBlankImy2017,
  AsyncBlankW9,
  AsyncBlankW92017,
  AsyncBlankW92018,
  AsyncBlankCrse,
  AsyncBlankUCrse2019,
  AsyncBlankCrseBba,
  AsyncBlankCrsCp,
  AsyncBlankUCrsCp2019,
  AsyncBlankCrsi,
  AsyncBlankRc519,
  AsyncBlankRc518
} from './asyncBlanks';

const FormSubmitConfirmation = () => (
  <div className="form-submit-confirmation">
    <p>By clicking &apos;Yes-Sign&apos; you confirm that you have reviewed the details in the electronic tax documents presented to you and you declare that all statements made in them are correct and complete.</p>
    <p>You will be required to electronically sign these document(s) on the next screen and you will no longer be able to make changes to them.</p>
    <p>Do you want to progress?</p>
  </div>
);

const sideBarSection = {
  validation: 'validation',
  review: 'review',
  overrides: 'overrides',
  supportingDocs: 'supportingDocs'
};

class BlankContainer extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    settings: PropTypes.shape(),
    permissions: PropTypes.arrayOf(PropTypes.string),
    isValidatingForm: PropTypes.bool.isRequired,
    validationResult: PropTypes.shape(),
    match: PropTypes.shape().isRequired,
    isGettingFormById: PropTypes.bool.isRequired,
    isFetchingPdfForm: PropTypes.bool,
    isFetchingPdfView: PropTypes.bool,
    instructionToggled: PropTypes.string,
    // eslint-disable-next-line
    pdfData: PropTypes.any,
    // eslint-disable-next-line
    viewPdfData: PropTypes.any,
    formByIdError: PropTypes.shape({
      code: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      message: PropTypes.string
    }),
    formByIdResponse: PropTypes.shape(),
    supportingDocs: PropTypes.arrayOf(PropTypes.shape()),
    getPdfViewError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
    formReviewState: PropTypes.shape(),
    workflowState: PropTypes.shape(),
    isAccountWithholdingShown: PropTypes.bool,
    dirty: PropTypes.bool,
    history: PropTypes.shape({
      go: PropTypes.func,
      block: PropTypes.func
    }).isRequired
  };

  static defaultProps = {
    settings: null,
    permissions: null,
    validationResult: null,
    formByIdResponse: null,
    formByIdError: null,
    supportingDocs: null,
    isFetchingPdfForm: false,
    isFetchingPdfView: false,
    pdfData: null,
    viewPdfData: null,
    getPdfViewError: null,
    instructionToggled: null,
    formReviewState: null,
    workflowState: null,
    isAccountWithholdingShown: false,
    dirty: false
  };

  static resizeTimer;

  static updateFormDimensions() {
    if (BlankContainer.resizeTimer) {
      clearInterval(BlankContainer.resizeTimer);
      BlankContainer.resizeTimer = null;
    }
    BlankContainer.resizeTimer = setInterval(() => {
      if (window.innerWidth < 1000) {
        toastr.info('You should use a monitor with a screen resolution higher than 1000 pixels to best view these forms.');
      }
      BlankContainer.adjustFormContainerHeight();
    }, 1000, this);
  }

  static adjustFormContainerHeight() {
    clearInterval(BlankContainer.resizeTimer);
    BlankContainer.resizeTimer = null;

    let height = 0;

    if (!document.getElementById('form-content')
    || !document.getElementById('form-content').firstChild
    || !document.getElementById('blankForm')) {
      return;
    }
    const pages = document.getElementById('form-content').firstChild.childNodes;

    if (!pages || pages.length === 0) {
      return;
    }

    let top = 0;
    let bottom = 0;
    for (let i = 0; i < pages.length; i += 1) {
      if (pages[i].className.includes('page')) {
        if (top === 0) {
          top = Math.ceil(pages[i].getBoundingClientRect().top);
        }
        bottom = Math.ceil(pages[i].getBoundingClientRect().bottom);
      }
    }

    if (top === 0 || bottom === 0) {
      return;
    }

    height = bottom - top;

    document.getElementById('form-content').style.height = `${ height }px`;
  }

  constructor(props) {
    super(props);
    this.state = {
      validationResultsVisible: true,
      originalVisible: false,
      originalFullScreen: false,
      pdfBlocked: false,
      pdfDownload: null,
      pdfView: null,
      showDocumentSignNotif: false,
      shownDocumentSignNotif: false,
      sideBarSectionVisible: sideBarSection.validation
    };

    this.unblock = props.history.block((location, action) => {
      const { dirty } = this.props;
      const { shownDocumentSignNotif } = this.state;
      if (shownDocumentSignNotif) {
        return null;
      }
      if (dirty) {
        return 'Are you sure you want to leave this page?';
      }
      return null;
    });

    this.onUnload = this.onUnload.bind(this);
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    const { params: { blankType, formId } } = match;
    dispatch(getFormById(blankType, formId));

    window.addEventListener('resize', BlankContainer.updateFormDimensions);
    window.addEventListener('beforeunload', this.onUnload);
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      dispatch,
      isGettingFormById,
      isValidatingForm,
      isFetchingPdfForm,
      isFetchingPdfView,
      formByIdResponse,
      match,
      instructionToggled
    } = this.props;
    const { validationResultsVisible, pdfDownload, pdfView, showDocumentSignNotif } = this.state;

    if (!this.isWorkflowBatchStaging() && newProps.formReviewState && newProps.formReviewState.workflowStage === FormWorkflowStages.BatchStaging) {
      this.setState({ sideBarSectionVisible: sideBarSection.supportingDocs });
    }

    if (match.url !== newProps.match.url) {
      const { params: { blankType, formId } } = newProps.match;
      dispatch(getFormById(blankType, formId));
      return;
    }

    if (isGettingFormById) {
      // New form being shown, clear any pdf's that are shown or downloaded
      if (pdfDownload) {
        URL.revokeObjectURL(pdfDownload);
      }
      if (pdfView) {
        URL.revokeObjectURL(pdfView);
      }

      this.setState({
        validationResultsVisible: (newProps.validationResult != null && newProps.validationResult.result != null),
        originalVisible: false,
        pdfView: null,
        pdfDownload: null
      });
    }

    if (isGettingFormById && !newProps.isGettingFormById) {
      setTimeout(() => {
        const el = document.getElementById('validation-results-btn');
        if (el) {
          el.click();
        }
      }, 1000);
    }

    if (newProps.instructionToggled && newProps.instructionToggled !== instructionToggled) {
      BlankContainer.updateFormDimensions();
    }

    if (isValidatingForm) {
      const isVisible = (newProps.validationResult != null
        && newProps.validationResult.result != null);
      this.setState({
        validationResultsVisible: validationResultsVisible || isVisible
      });
    }

    if (!showDocumentSignNotif
      && newProps.formByIdResponse != null && newProps.formByIdResponse.isOnlineForm
      && newProps.validationResult != null) {
      const { params: { blankType } } = newProps.match;
      const instrFound = newProps.validationResult.result[0] != null
        && newProps.validationResult.result[0].warnings != null
        && newProps.validationResult.result[0].rejections != null
        && newProps.validationResult.result[0].rejections.length === 0
        && (((blankType === 'w9' || blankType === 'w9_2017' || blankType === 'w9_2018') && newProps.validationResult.result[0].warnings.findIndex(el => el.code === 'REC000240') !== -1)
        || ((blankType === 'w8bene' || blankType === 'w8bene_2017' || blankType === 'w8bene_2021') && newProps.validationResult.result[0].warnings.findIndex(el => el.code === 'REC000890') !== -1));

      if (instrFound) {
        this.setState({
          showDocumentSignNotif: true
        });
      }
    }

    if (isFetchingPdfForm && !newProps.isFetchingPdfForm && newProps.pdfData) {
      this.setState({
        pdfDownload: newProps.pdfData
      });
      const link = document.createElement('a');
      link.href = newProps.pdfData;
      link.setAttribute('download', (formByIdResponse && formByIdResponse.originalFileName) ? formByIdResponse.originalFileName : 'form.pdf');
      document.body.appendChild(link);
      link.click();
    } else {
      this.setState({
        pdfDownload: null
      });
    }

    if (!pdfView && !newProps.getPdfViewError) {
      if (isFetchingPdfView && !newProps.isFetchingPdfView && newProps.viewPdfData) {
        this.setState({
          originalVisible: true,
          pdfView: newProps.viewPdfData
        });
      } else {
        if (isFetchingPdfView && !newProps.isFetchingPdfView) {
          if (newProps.viewPdfData == null) {
            toastr.confirm('The original form is too large to be displayed next to the digitized form. To view it, please download it using the \'Download original PDF\' button', { disableCancel: true });
          }
          this.setState({ pdfBlocked: true });
        }
        this.setState({
          originalVisible: false,
          pdfView: null
        });
      }
    }
  }

  componentDidUpdate() {
    BlankContainer.updateFormDimensions();
  }

  componentWillUnmount() {
    const { pdfDownload, pdfView } = this.state;
    window.removeEventListener('resize', BlankContainer.updateFormDimensions);

    if (pdfDownload) {
      URL.revokeObjectURL(pdfDownload);
    }

    if (pdfView) {
      URL.revokeObjectURL(pdfView);
    }

    this.unblock();
    window.removeEventListener('beforeunload', this.onUnload);
  }

  onUnload(event) {
    const { dirty } = this.props;
    if (dirty != null && dirty) {
      event.returnValue = 'Are you sure you want to leave this page?';
    }
  }

  onValidateForm(blankType, values) {
    const { dispatch, match, formByIdResponse, validationResult } = this.props;
    const { showDocumentSignNotif, shownDocumentSignNotif } = this.state;
    const { params: { formId } } = match;

    if (showDocumentSignNotif && !shownDocumentSignNotif
      && formByIdResponse != null && formByIdResponse.isOnlineForm
      && validationResult != null) {
      const toastrOptions = {
        cancelText: 'Yes - Sign',
        okText: 'No - go back',
        component: () => (
          <FormSubmitConfirmation />
        )
      };

      toastr.confirm(null, toastrOptions);
      this.setState({
        shownDocumentSignNotif: true
      });
    }
    dispatch(validateForm(blankType, formId, values));
  }

  getBlankComponent(BlankComponent) {
    const { match: { params: { blankType } } } = this.props;
    return (
      <BlankComponent
        onSubmit={ values => this.onValidateForm(blankType, values) }
      />
    );
  }

  getPdfFile(formId) {
    const { dispatch } = this.props;
    const { pdfDownload } = this.state;

    if (!pdfDownload) {
      dispatch(getFormPdf(formId));
    }
  }

  getOriginalPdfView(formId) {
    const { dispatch } = this.props;
    const { pdfView } = this.state;

    if (!pdfView) {
      dispatch(getPdfView(formId));
      return false;
    }
    return true;
  }

  getWrapperClasses(blankType) {
    const { originalVisible, validationResultsVisible } = this.state;
    let classes = 'blank-wrapper';

    classes += blankType && blankType.indexOf('w') === 0 ? ' w-blank-wrapper' : '';
    classes += blankType && (blankType.indexOf('dt_crs') === 0 || blankType.indexOf('crs') === 0 || blankType.indexOf('ucrs') === 0) ? ' crs-blank-wrapper' : '';
    classes += blankType && blankType.indexOf('rc') === 0 ? ' rc-blank-wrapper' : '';

    classes += originalVisible ? ' side-by-side' : '';
    classes += originalVisible && !validationResultsVisible ? ' val-collapsed' : '';

    return classes;
  }

  getFormValidState() {
    const { validationResult } = this.props;

    if (!validationResult || !validationResult.result) {
      return 'validation-result-icon none';
    }
    if (validationResult != null && validationResult.valid != null && validationResult.valid !== 'undefined') {
      return `validation-result-icon ${ ((validationResult.valid) ? 'valid' : 'invalid') }`;
    }
    return '';
  }

  getFormSectionClasses() {
    const { validationResultsVisible, originalVisible } = this.state;
    return `form-section ${ !originalVisible && !validationResultsVisible ? 'expand' : '' }`;
  }

  getValidationResultsSectionClasses() {
    const { validationResultsVisible } = this.state;
    return `validation-results-wrapper ${ !validationResultsVisible ? 'hide-content' : '' }`;
  }

  getOriginalSectionClasses() {
    const { originalFullScreen } = this.state;
    return `original-section ${ originalFullScreen ? 'expand' : '' }`;
  }

  getFormContentClasses() {
    const { permissions } = this.props;

    if (permissions === null) {
      return 'form-content';
    }

    if (this.isWorkflowComplete() || this.isViewOnlyForm()) {
      return 'form-content view-only';
    }
    return 'form-content';
  }

  getFormValidityStatus() {
    const { validationResult } = this.props;
    let statusText = '';
    if (validationResult) {
      statusText = validationResult.valid ? 'Valid' : 'Invalid';
      statusText += validationResult.result && validationResult.result[0] && validationResult.result[0].isWithTreaty ? ' with Treaty' : '';
      statusText += validationResult.result && validationResult.result[0] && validationResult.result[0].isLocked ? ' & Locked' : '';
    }

    return statusText;
  }

  getSupportingDocsBadgeCount() {
    const { supportingDocs } = this.props;
    const docCount = supportingDocs ? supportingDocs.length : 0;
    let badgeCount = '0';
    if (docCount) {
      badgeCount = docCount > 99 ? '99+' : docCount.toString();
    }
    return badgeCount;
  }

  getSupportingDocsBadgeClass() {
    const { supportingDocs } = this.props;
    let badgeClass = 'supporting-docs-icon';
    if (supportingDocs && supportingDocs.length) {
      badgeClass = 'supporting-docs-icon redbadge badge';
    }
    return badgeClass;
  }

  toggleSideBarSection = section => this.setState({ sideBarSectionVisible: section });

  isValidated() {
    const { validationResult } = this.props;
    return (validationResult != null && validationResult.result != null);
  }

  isValidForm() {
    const { validationResult } = this.props;
    return (validationResult && validationResult.valid);
  }

  isLocked() {
    const { validationResult } = this.props;
    return (validationResult && validationResult.result && validationResult.result[0] && validationResult.result[0].isLocked);
  }

  isWorkflowBatchStaging() {
    const { formReviewState } = this.props;
    return (formReviewState && formReviewState.workflowStage === FormWorkflowStages.BatchStaging);
  }

  isWorkflowComplete() {
    const { formReviewState } = this.props;
    return (formReviewState != null && formReviewState.workflowStage === FormWorkflowStages.Complete);
  }

  isValidReviewer() {
    const { permissions, formReviewState, workflowState } = this.props;

    if (formReviewState == null) {
      return false;
    }

    // WF Stage 1 or 2 - Draft or Pending Signature
    if (formReviewState.workflowStage === FormWorkflowStages.Draft || formReviewState.workflowStage === FormWorkflowStages.PendingSignature) {
      return true;
    }

    // WF Stage 3 - Batch Staging
    if (formReviewState.workflowStage === FormWorkflowStages.BatchStaging) {
      return false;
    }

    const isUserFormOwner = workflowState != null && workflowState.assignedUser === getStorageSetting('username');

    // WF Stage 4 or 5 and Initial Reviewer
    // WF Stage 6 or 7 and Secondary Reviewer
    // WF Stage 8, 9 or 100 and Expert Reviewer
    // WF Stage 100 and Supplementary Reviewer
    const initialReviewer = (formReviewState.workflowStage === FormWorkflowStages.PendingReview || formReviewState.workflowStage === FormWorkflowStages.UnderInitialReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer]);
    const secondaryReviewer = (formReviewState.workflowStage === FormWorkflowStages.PendingSecondaryReview || formReviewState.workflowStage === FormWorkflowStages.UnderSecondaryReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.fwfSecondaryReviewer]);
    const expertReviewer = (formReviewState.workflowStage === FormWorkflowStages.PendingExpertReview || formReviewState.workflowStage === FormWorkflowStages.UnderExpertReview || formReviewState.workflowStage === FormWorkflowStages.Complete)
      && AccessControl.hasPermission(permissions, [ActivityPerms.fwfExpertReviewer]);
    const suppReviewer = formReviewState.workflowStage === FormWorkflowStages.Complete && AccessControl.hasPermission(permissions, [ActivityPerms.fwfSupplReviewer]);

    if (formReviewState.workflowStage === FormWorkflowStages.Complete) {
      return (expertReviewer || suppReviewer);
    }

    return (isUserFormOwner && (initialReviewer || secondaryReviewer || expertReviewer || suppReviewer));
  }

  isDraft() {
    const { formReviewState } = this.props;

    if (!formReviewState) {
      return false;
    }

    // WF Stage 1 or 2 - Draft or Pending Signature
    return (formReviewState.workflowStage === FormWorkflowStages.Draft || formReviewState.workflowStage === FormWorkflowStages.PendingSignature);
  }

  isOnlineForm() {
    const { formByIdResponse } = this.props;
    return (formByIdResponse != null && (formByIdResponse.isOnlineForm || !formByIdResponse.originalFileName));
  }

  isViewOnlyForm() {
    const { permissions, formReviewState, workflowState } = this.props;

    if (permissions === null) {
      return false;
    }

    if (this.isLocked()) {
      return true;
    }

    if (formReviewState == null || formReviewState.workflowStage === FormWorkflowStages.BatchStaging) {
      return true;
    }

    const isUserFormOwner = workflowState != null && workflowState.assignedUser === getStorageSetting('username');

    if (this.isOnlineForm()) {
      if (!isUserFormOwner || !AccessControl.hasPermission(permissions, [ActivityPerms.electronicValidate])) {
        return true;
      }
      return !AccessControl.hasPermission(permissions, [ActivityPerms.electronicView, ActivityPerms.electronicValidate]);
    }

    if (!isUserFormOwner || !AccessControl.hasPermission(permissions, [ActivityPerms.scannedValidate, ActivityPerms.scannedEdit])) {
      return true;
    }
    return !AccessControl.hasPermission(permissions, [ActivityPerms.scannedView, ActivityPerms.scannedValidate, ActivityPerms.scannedEdit]);
  }

  isInImyTreeStructure() {
    const {
      match: { params: { blankType } },
      formByIdResponse
    } = this.props;
    return (blankType === 'w8imy' || blankType === 'w8imy_2017' || (formByIdResponse && formByIdResponse.parentFormId));
  }

  navigateToImyStructure() {
    const { dispatch, match: { params: { formId } }, formByIdResponse } = this.props;
    dispatch(push(`/w8imystructure/${ formByIdResponse != null && formByIdResponse.parentFormId != null ? formByIdResponse.parentFormId : formId }`));
  }

  toggleValidationSection() {
    const { validationResultsVisible } = this.state;
    this.setState({ validationResultsVisible: !validationResultsVisible });
    return false;
  }

  toggleOriginal(formId) {
    const { originalVisible, pdfView } = this.state;

    if (!originalVisible && !pdfView) {
      this.getOriginalPdfView(formId);
    } else {
      this.setState({
        originalVisible: !originalVisible,
        originalFullScreen: false,
        validationResultsVisible: this.isValidated()
      });
    }
    return false;
  }

  openAccountWithholdingModal(item) {
    const { dispatch, match: { params: { formId } }, formByIdResponse } = this.props;
    if (formByIdResponse != null && formByIdResponse.hasImyTopLevelAccount && (formByIdResponse.hasAllocationStatement || formByIdResponse.hasParentForm)) {
      const parentFormId = formByIdResponse != null && formByIdResponse.parentFormId != null ? formByIdResponse.parentFormId : formId;
      dispatch(showImyTreeWithholding(parentFormId));
    } else {
      dispatch(showAccountWithholdingModal(item));
    }
  }

  closeAccountWithholdingModal() {
    const { dispatch } = this.props;
    dispatch(hideAccountWithholdingModal());
  }

  renderBlank() {
    const { match: { params: { blankType } } } = this.props;
    switch (blankType) {
      case 'w8bene':
        return this.getBlankComponent(AsyncBlankBene);
      case 'w8bene_2017':
        return this.getBlankComponent(AsyncBlankBene2017);
      case 'w8bene_2021':
        return this.getBlankComponent(AsyncBlankBene2021);
      case 'w8ben':
        return this.getBlankComponent(AsyncBlankBen);
      case 'w8ben_2014':
        return this.getBlankComponent(AsyncBlankBen2014);
      case 'w8ben_2021':
        return this.getBlankComponent(AsyncBlankBen2021);
      case 'w8eci_2014':
        return this.getBlankComponent(AsyncBlankEci);
      case 'w8eci':
        return this.getBlankComponent(AsyncBlankEci2017);
      case 'w8eci_2021':
        return this.getBlankComponent(AsyncBlankEci2021);
      case 'w8exp_2016':
        return this.getBlankComponent(AsyncBlankExp);
      case 'w8exp':
        return this.getBlankComponent(AsyncBlankExp2017);
      case 'w8imy':
        return this.getBlankComponent(AsyncBlankImy);
      case 'w8imy_2017':
        return this.getBlankComponent(AsyncBlankImy2017);
      case 'w9':
        return this.getBlankComponent(AsyncBlankW9);
      case 'w9_2017':
        return this.getBlankComponent(AsyncBlankW92017);
      case 'w9_2018':
        return this.getBlankComponent(AsyncBlankW92018);
      case 'crse':
      case 'dt_crse':
        return this.getBlankComponent(AsyncBlankCrse);
      case 'ucrse_2019':
        return this.getBlankComponent(AsyncBlankUCrse2019);
      case 'crsebba':
        return this.getBlankComponent(AsyncBlankCrseBba);
      case 'crscp':
      case 'dt_crscp':
        return this.getBlankComponent(AsyncBlankCrsCp);
      case 'ucrscp_2019':
        return this.getBlankComponent(AsyncBlankUCrsCp2019);
      case 'crsi':
      case 'dt_crsi':
        return this.getBlankComponent(AsyncBlankCrsi);
      case 'rc519':
        return this.getBlankComponent(AsyncBlankRc519);
      case 'rc518':
        return this.getBlankComponent(AsyncBlankRc518);
      default:
        return <div>Incorrect blank type</div>;
    }
  }

  render() {
    const {
      settings,
      permissions,
      isValidatingForm,
      validationResult,
      match: { params: { formId, blankType } },
      isGettingFormById,
      isFetchingPdfForm,
      isFetchingPdfView,
      formByIdError,
      formByIdResponse,
      supportingDocs,
      formReviewState,
      workflowState,
      getPdfViewError,
      isAccountWithholdingShown
    } = this.props;
    const { originalVisible, pdfDownload, pdfView, pdfBlocked, sideBarSectionVisible, validationResultsVisible } = this.state;

    const isBatchStaging = this.isWorkflowBatchStaging();
    const hasReviewerPermission = !isBatchStaging && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer, ActivityPerms.fwfSecondaryReviewer, ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);
    const hasSupportingDocsPermission = AccessControl.hasPermission(permissions, [ActivityPerms.scannedSupDocs]);
    const hasWithholdingPermission = !isBatchStaging && settings && settings.showWithholding && AccessControl.hasPermission(permissions, [ActivityPerms.whdViewRates]);
    const canViewImyTree = !isBatchStaging && formByIdResponse
      && ((formByIdResponse.hasAllocationStatement && (blankType === 'w8imy' || blankType === 'w8imy_2017'))
      || formByIdResponse.hasParentForm)
      && AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]);
    return (
      <div>
        { (isGettingFormById || formByIdError != null) && <div className="blank-wrapper">
          <BlankPage>
            <LoadingWrapper
              loaded={ !isGettingFormById }
            >
              { formByIdError && (formByIdError.code !== 0
                ? <NotFoundPage caption="The form was not found" />
                : <div className="toast toast-error">
                  { formByIdError.message }
                </div>) }
            </LoadingWrapper>
          </BlankPage>
        </div> }

        { !isGettingFormById && formByIdError == null && blankType != null && <div
          className={ this.getWrapperClasses(blankType) }
        >
          <div className={ this.getOriginalSectionClasses() }>
            {
              originalVisible && isFetchingPdfView
              && <LoadingWrapper loaded={ !isFetchingPdfView } />
            }
            {

            }
            { originalVisible && !isFetchingPdfView && !getPdfViewError && <ScrollPdfViewer
              file={ pdfView }
            /> }
            { !isFetchingPdfView && getPdfViewError && <div className="react-pdf__message">
              <div className="viewer--error"><p>{ getPdfViewError.message != null ? getPdfViewError.message : '' }</p></div>
            </div> }
          </div>
          <div className={ this.getFormSectionClasses() }>
            <div id="form-wrapper" className="form-wrapper">
              <div id="form-content" className={ this.getFormContentClasses() }>
                { (this.isWorkflowComplete() || this.isViewOnlyForm()) && <div className="form-overlay" /> }
                { this.renderBlank() }
              </div>
            </div>
          </div>
          <div className={ this.getValidationResultsSectionClasses() }>
            { !isBatchStaging && sideBarSectionVisible === sideBarSection.validation && <ValidationResultsSection
              validationResult={ validationResult }
              isValidatingForm={ isValidatingForm }
              isOnlineForm={ formByIdResponse != null && formByIdResponse.isOnlineForm }
              showWitholdingValidation={ (blankType === 'w8imy' || blankType === 'w8imy_2017')
              && validationResult != null && validationResult.whdResult != null }
              accountNumber={ formByIdResponse && formByIdResponse.accountNumber }
            /> }
            { !isBatchStaging && sideBarSectionVisible === sideBarSection.review && <FormReviewerComments
              formId={ !Number.isNaN(formId) ? parseInt(formId, 10) : null }
              formReviewState={ formReviewState }
              validReviewer={ this.isValidReviewer() }
              isLocked={ this.isLocked() }
              hasValidationResult={ validationResult != null }
            /> }
            { sideBarSectionVisible === sideBarSection.overrides && <IndividualValidationOverrides
              permissions={ permissions }
              workflowStage={ formReviewState && formReviewState.workflowStage }
              workflowUsername={ workflowState && workflowState.assignedUser }
              formId={ validationResult.result[0].formId }
              customerId={ validationResult.result[0].customerId }
              customerName={ validationResult.result[0].customerName }
              formType={ validationResult.result[0].formType }
              key={ validationResult.result[0].controlId }
              isOnlineForm={ formByIdResponse.isOnlineForm }
              valid={ validationResult.valid }
              rejections={ validationResult.result[0].rejections }
              warnings={ validationResult.result[0].warnings }
              overrides={ validationResult.result[0].overrides }
              isWithTreaty={ validationResult.result[0].isWithTreaty }
              isLocked={ validationResult.result[0].isLocked }
            /> }
            { sideBarSectionVisible === sideBarSection.supportingDocs && <SupportingDocs
              workflowStage={ formReviewState && formReviewState.workflowStage }
              formId={ !Number.isNaN(formId) ? parseInt(formId, 10) : null }
              documents={ supportingDocs }
              workflowUsername={ workflowState && workflowState.assignedUser }
              formType={ validationResult ? validationResult.result[0].formType : blankType }
              isLocked={ this.isLocked() }
            /> }
          </div>
          <div id="icon-bar" className="icon-bar">
            { !isBatchStaging && <button
              id="validation-results-btn"
              type="button"
              className={ this.getFormValidState() }
              onClick={ () => this.toggleSideBarSection(sideBarSection.validation) }
              title="Validation Results"
            >
              &nbsp;
            </button> }
            { hasReviewerPermission
              && <button
                type="button"
                className="form-review-icon"
                onClick={ () => this.toggleSideBarSection(sideBarSection.review) }
                title="Reviewer Comments"
              >
                &nbsp;
              </button> }
            { hasReviewerPermission
              && validationResult
              && validationResult.result
              && !this.isLocked()
              && <button
                type="button"
                className="override-icon"
                onClick={ () => this.toggleSideBarSection(sideBarSection.overrides) }
                title="Validation Overrides"
              >
                &nbsp;
              </button> }
            { canViewImyTree && <button
              type="button"
              className="imy-tree-icon"
              onClick={ () => this.navigateToImyStructure() }
              title="W-8IMY Structure"
            >&nbsp;</button> }
            { (hasSupportingDocsPermission || hasReviewerPermission)
              && <button
                type="button"
                className={ this.getSupportingDocsBadgeClass() }
                onClick={ () => this.toggleSideBarSection(sideBarSection.supportingDocs) }
                title="Supporting Documents"
                data-badge={ this.getSupportingDocsBadgeCount() }
              >
                &nbsp;
              </button> }
            { hasWithholdingPermission
              && formByIdResponse != null
              && ((formByIdResponse.hasParentForm && formByIdResponse.hasImyTopLevelAccount) || formByIdResponse.accountId != null)
              && <button
                type="button"
                className="withholding-rates-icon"
                onClick={ () => this.openAccountWithholdingModal(formByIdResponse) }
                title="Show Withholding Rates"
              >
                &nbsp;
              </button> }
            { !isBatchStaging && <button type="button" className={ validationResultsVisible ? 'collapse-icon' : 'expand-icon' } onClick={ () => this.toggleValidationSection() } title="Click to expand/collapse">&nbsp;</button> }
          </div>
          <WithholdingRatesModal
            isShown={ isAccountWithholdingShown }
            onClose={ () => this.closeAccountWithholdingModal() }
          />
        </div> }

        { !formByIdError && <div className="fixed-elements">
          <div className="left-side">
            { !originalVisible && <RedirectButton
              to="/"
              title="Return back"
              className="btn-redirect-home"
              redirectBack
            /> }
            { originalVisible && <button
              type="button"
              title="Close original form"
              className="btn"
              disabled={ isGettingFormById || isFetchingPdfView || pdfBlocked }
              onClick={ () => this.toggleOriginal(formId) }
            >
              Close original form
            </button> }
          </div>
          <div className="center">
            { (this.isValidReviewer() && !this.isWorkflowComplete() && AccessControl.hasPermission(permissions,
              [ActivityPerms.scannedValidate, ActivityPerms.scannedEdit])) && <button
                type="button"
                form="blankForm"
                className="btn btn-primary"
                disabled={ [].includes(blankType) || isGettingFormById || this.isLocked() }
                onClick={ () => document.getElementById('dummy-validate-form-btn').click() }
              >
                Validate form
              </button> }

            { !originalVisible && formByIdResponse != null && formByIdResponse.hasSavedPdf && <button
              type="button"
              title="Open original form"
              className="btn"
              disabled={ isGettingFormById || isFetchingPdfView || pdfBlocked }
              onClick={ () => this.toggleOriginal(formId) }
            >
              { isFetchingPdfView && <span className="icon loading" /> }
              Open original form
            </button> }
          </div>
          <div className="right-side">
            { formByIdResponse != null && formByIdResponse.hasSavedPdf && <AccessControl allowedPermissions={ [ActivityPerms.scannedDownload, ActivityPerms.electronicDownload] }>
              <a
                title="Download original PDF"
                role="button"
                tabIndex="-1"
                href={ pdfDownload }
                target="_blank"
                rel="noopener noreferrer"
                className="btn pdf-preview-link"
                onClick={ () => this.getPdfFile(formId) }
                disabled={ isFetchingPdfForm }
                download={ !isFetchingPdfForm && pdfDownload && (formByIdResponse && formByIdResponse.originalFileName) ? formByIdResponse.originalFileName : 'form.pdf' }
              >
                <span>
                  { !isFetchingPdfForm ? <img src="assets/images/pdf-icon.svg" alt="PDF icon" className="pdf-icon" /> : <span className="icon loading" /> }
                  Download original PDF
                </span>
              </a>
            </AccessControl>}
          </div>
        </div> }
      </div>
    );
  }
}

function getReduxFormDirtyStatus(state, ownProps) {
  let reduxFormName = '';
  if (ownProps != null && ownProps.match != null && ownProps.match.params != null && ownProps.match.params.blankType != null) {
    let formName = ownProps.match.params.blankType;
    const splitIndex = ownProps.match.params.blankType.indexOf('_');
    if (splitIndex > -1) {
      formName = ownProps.match.params.blankType.substring(0, splitIndex);
    }
    reduxFormName = `${ formName }-form`;
  }
  return isDirty(reduxFormName)(state);
}

function mapStateToProps(state, ownProps) {
  const {
    auth: {
      settings,
      permissions
    },
    forms: {
      isValidatingForm,
      validationResult,
      isGettingFormById,
      isFetchingPdfForm,
      isFetchingPdfView,
      pdfData,
      formByIdError,
      formByIdResponse,
      supportingDocs,
      formReviewState,
      workflowState,
      viewPdfData,
      getPdfViewError,
      instructionToggled,
      isAccountWithholdingShown
    }
  } = state;

  const dirty = getReduxFormDirtyStatus(state, ownProps);

  return {
    settings,
    permissions,
    isValidatingForm,
    validationResult,
    isGettingFormById,
    isFetchingPdfForm,
    isFetchingPdfView,
    pdfData,
    formByIdError,
    formByIdResponse,
    supportingDocs,
    formReviewState,
    workflowState,
    viewPdfData,
    getPdfViewError,
    instructionToggled,
    isAccountWithholdingShown,
    dirty
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(BlankContainer, 'BlankContainer'));
